import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDuration } from './DealTabs'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
} from './record'
import { RecordPicker } from './RecordPicker'
import { CopyValue, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import {
  assignPicker,
  cancelPickTask,
  completePickTask,
  fetchPickRoute,
  fetchPickTask,
  fetchStockOf,
  type PickRoute,
  type PickTask,
} from './warehouseApi'
import {
  CellCode,
  HistoryTab,
  OperationAlerts,
  StatusBadge,
  cancelledEnd,
  copyLinkItem,
  documentState,
  momentOr,
  newOperationKey,
  qty,
  short,
  stagesOf,
  useOperation,
} from './warehouseRecord'
import '@/styles/record.css'

const STAGES = ['new', 'assigned', 'in_progress', 'completed'] as const

/** Остановка маршрута, собранная из маршрута сервера и строки задания. */
interface Stop {
  lineId: string
  order: number
  cell: string
  productId: string
  name: string | undefined
  quantity: number
  picked: number | null
  status: string
  next: boolean
}

/**
 * Карточка задания на сборку на дизайн-системе (Picking.dc.html).
 *
 * Маршрут — строки `.pick-row` в порядке, **присланном сервером** (`/wms/pick-tasks/{id}/route`):
 * сервер дополняет координаты ячеек до фиксированной ширины, и без этого стойка «10»
 * встала бы перед «2». Пересортировать здесь значило бы отменить эту работу. Следующую
 * остановку тоже называет сервер (`nextStopIndex`): правило «первая незавершённая» одно, и
 * терминал с вебом не покажут разные следующие ячейки. Взятое количество — из офисной
 * карточки задания: в маршруте его нет.
 *
 * Действия в шапке: назначить сборщика, завершить сборку (под `update`, когда не осталось
 * необработанных строк — иначе домен откажет), отмена в «⋯» через подтверждение: она
 * снимает резервы. Подтверждение строк — только сканом на ТСД.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Причина пропуска («ячейка пуста»), лента сканов, ТСД сборщика, норма времени и SLA,
 *   выработка сборщика за смену, зона отгрузки и «Куда везти»: API этого не отдаёт.
 * - «Добавить в маршрут» другую ячейку и «Печать маршрута»: таких операций нет. Где ещё
 *   лежит пропущенный товар — показано по остаткам склада, но добавить ячейку нельзя.
 * - Отгрузка и клиент задания: волна собирает несколько заказов, и связи «задание →
 *   отгрузка» в карточке нет.
 */
export function PickRecordView({ recordId, record, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const [tab, setTab] = useState('route')
  const [assigning, setAssigning] = useState(false)
  const [assignee, setAssignee] = useState<{ id: string; label: string } | null>(null)
  const [assigneeId, setAssigneeId] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)
  const operation = useOperation([
    ['pick-task', recordId],
    ['pick-route', recordId],
    ['record', 'warehouse.pick', recordId],
    ['list', 'warehouse.pick'],
  ])

  const mayUpdate = can(user, 'warehouse.pick', 'update')
  const mayCancel = can(user, 'warehouse.pick', 'delete')
  const mayPickEmployee = can(user, 'hr.employee', 'read')

  const taskQuery = useQuery({
    queryKey: ['pick-task', recordId],
    queryFn: ({ signal }) => fetchPickTask(recordId, signal),
    retry: false,
  })
  const route = useQuery({
    queryKey: ['pick-route', recordId],
    queryFn: ({ signal }) => fetchPickRoute(recordId, signal),
    retry: false,
  })

  const assign = useMutation({
    mutationFn: (employeeId: string) => assignPicker(recordId, employeeId),
    onSuccess: () => {
      setAssigning(false)
      setAssignee(null)
      setAssigneeId('')
      operation.succeed(t('card.wh.pick.assigned'))
    },
    onError: operation.fail,
  })
  const complete = useMutation({
    mutationFn: (key: string) => completePickTask(recordId, key),
    onSuccess: (shortages) =>
      operation.succeed(
        shortages.length > 0 ? t('record.wh.pick.completedShort', { count: shortages.length }) : t('record.wh.pick.completed'),
      ),
    onError: operation.fail,
  })
  const cancel = useMutation({
    mutationFn: () => cancelPickTask(recordId),
    onSuccess: () => {
      setConfirmCancel(false)
      operation.succeed(t('card.wh.pick.cancelled'))
    },
    onError: (error) => {
      setConfirmCancel(false)
      operation.fail(error)
    },
  })

  const gate = documentState(taskQuery, 'warehouse.pick', '/picking', t)
  if (gate) return gate
  const task = taskQuery.data as PickTask

  const stops = stopsOf(task, route.data)
  const pending = stops.filter((stop) => stop.status === 'pending')
  const skipped = stops.filter((stop) => stop.status === 'skipped')
  const short_ = stops.filter((stop) => stop.status === 'short')
  const taken = stops.filter((stop) => stop.status === 'picked' || stop.status === 'short')
  const done = stops.length - pending.length
  const percent = stops.length > 0 ? Math.round((done / stops.length) * 100) : 0
  const active = task.status !== 'completed' && task.status !== 'cancelled'

  // ------------------------------------------------------------------ шапка

  const assigneeLabel = labelOf(record.values, 'assignee_employee_id')
  const warehouseLabel = labelOf(record.values, 'warehouse_id')
  const meta = [
    <StatusBadge key="status" entity="warehouse.pick" status={task.status} />,
    <span key="mode">{t(`value.warehouse.pick.pick_mode.${task.mode}`)}</span>,
    warehouseLabel ? (
      <span key="warehouse" className="row" style={{ gap: 6 }}>
        <Icon name="box" size="sm" className="muted" />
        {t('record.wh.warehouseNamed', { name: warehouseLabel })}
      </span>
    ) : null,
    <span key="picker" className="row" style={{ gap: 6 }}>
      <Icon name="user" size="sm" className="muted" />
      {task.assigneeEmployeeId
        ? t('record.wh.pick.picker', { name: assigneeLabel ?? short(task.assigneeEmployeeId) })
        : t('record.wh.pick.noPicker')}
    </span>,
    <CopyValue key="copy" value={task.number} label={t('record.copyNumber', { number: task.number })} />,
  ]

  const secondary: HeaderAction[] =
    mayUpdate && active
      ? [
          {
            id: 'assign',
            label: task.assigneeEmployeeId ? t('record.wh.pick.reassign') : t('card.wh.pick.assignSubmit'),
            icon: 'user',
            onClick: () => {
              operation.clear()
              setAssigning(true)
              if (tab === 'history') setTab('route')
            },
          },
        ]
      : []
  // Unresolved lines left: the domain refuses to complete, so the button says so instead
  // of failing.
  const primary: HeaderAction | null =
    mayUpdate && task.status === 'in_progress'
      ? {
          id: 'complete',
          label: t('record.wh.pick.complete'),
          icon: 'check',
          disabled: pending.length > 0 || complete.isPending,
          onClick: () => {
            operation.clear()
            complete.mutate(newOperationKey())
          },
        }
      : null

  const menu: MenuAction[] = [copyLinkItem(t, operation.setFailure)]
  if (mayCancel && active) {
    menu.push({
      id: 'cancel',
      label: t('card.wh.pick.cancelSubmit'),
      icon: 'close',
      danger: true,
      onSelect: () => {
        operation.clear()
        setConfirmCancel(true)
      },
    })
  }

  // ------------------------------------------------------------------ факты

  const started = record.values.started_at
  const finished = record.values.completed_at
  const elapsed =
    typeof started === 'string'
      ? Math.max(0, ((typeof finished === 'string' ? new Date(finished) : new Date()).getTime() - new Date(started).getTime()) / 1000)
      : null
  const facts: Fact[] = [
    {
      id: 'rows',
      label: t('record.wh.pick.rows'),
      value: qty(stops.length, locale),
      sub: t('record.wh.pick.rowsSub', { taken: taken.length, skipped: skipped.length, left: pending.length }),
    },
    {
      id: 'done',
      label: t('record.wh.pick.done'),
      value: (
        <span className="stack" style={{ gap: 4 }}>
          <span>{t('record.wh.of', { done, total: stops.length })}</span>
          <span
            className="meter"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('record.wh.pick.doneLabel', { done, total: stops.length })}
          >
            <i style={{ width: `${percent}%` }} />
          </span>
        </span>
      ),
      sub: short_.length > 0 ? t('record.wh.pick.shortSub', { count: short_.length }) : undefined,
    },
    {
      id: 'skipped',
      label: t('record.wh.pick.skipped'),
      value:
        skipped.length > 0 ? (
          <span className="flag flag-warning" style={{ fontSize: 16, fontWeight: 600 }}>
            <Icon name="alert" />
            {qty(skipped.length, locale)}
          </span>
        ) : (
          <span className="muted">0</span>
        ),
      sub: skipped.length > 0 ? <span className="mono">{skipped.map((stop) => stop.cell).join(', ')}</span> : undefined,
    },
    {
      id: 'time',
      label: t('record.wh.pick.time'),
      value: elapsed == null ? '—' : formatDuration(elapsed, t),
      sub: typeof started === 'string' ? t('record.wh.pick.since', { at: momentOr(started, locale, '') }) : t('record.wh.pick.notStarted'),
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const routePanel =
    stops.length === 0 ? (
      <StateView boxed kind="empty" title={t('card.wh.pick.noLines')} description={t('record.wh.pick.emptyText')} />
    ) : (
      <Panel
        title={t('record.wh.pick.route')}
        count={stops.length}
        flush
        actions={route.isError ? <span className="t-sm muted">{t('record.wh.pick.routeFallback')}</span> : undefined}
      >
        <div role="list" aria-label={t('record.wh.pick.route')} className="wh-route">
          {stops.map((stop) => (
            <RouteRow key={stop.lineId} stop={stop} />
          ))}
        </div>
        <div className="panel-f secondary">{t('record.wh.pick.routeNote')}</div>
      </Panel>
    )

  const skippedPanels =
    skipped.length === 0 ? (
      <StateView boxed kind="empty" title={t('record.wh.pick.noSkipped')} />
    ) : (
      <>
        {skipped.map((stop) => (
          <SkippedPanel key={stop.lineId} stop={stop} warehouseId={task.warehouseId} />
        ))}
      </>
    )

  const sidebar = (
    <div className="rec-col">
      <Panel title={t('record.wh.pick.pickerPanel')}>
        {assigning && mayUpdate ? (
          <div className="stack" style={{ gap: 8 }}>
            {mayPickEmployee ? (
              <RecordPicker
                id="pick-assignee"
                entity="hr.employee"
                searchColumn="last_name"
                extraColumn="personnel_number"
                value={assignee}
                onChange={setAssignee}
              />
            ) : (
              // No right to read employees: the list to pick from would be refused, so the
              // identifier is typed in and the gap is said out loud.
              <label className="stack" style={{ gap: 4 }}>
                <span className="t-sm">{t('card.wh.pick.assignee')}</span>
                <input className="input" type="text" value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)} />
                <span className="t-xs muted">{t('card.wh.pick.assigneeHint')}</span>
              </label>
            )}
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={assign.isPending || (mayPickEmployee ? assignee == null : assigneeId.trim() === '')}
                onClick={() => assign.mutate(mayPickEmployee ? (assignee?.id ?? '') : assigneeId.trim())}
              >
                {t('card.wh.pick.assignSubmit')}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAssigning(false)}>
                {t('card.cancel')}
              </button>
            </div>
          </div>
        ) : task.assigneeEmployeeId ? (
          <div className="row" style={{ gap: 12 }}>
            <span className="ava-xl" aria-hidden="true">
              {(assigneeLabel ?? '?')
                .split(/\s+/)
                .slice(0, 2)
                .map((part) => part[0] ?? '')
                .join('')}
            </span>
            <span className="strong t-md">{assigneeLabel ?? short(task.assigneeEmployeeId)}</span>
          </div>
        ) : (
          <p className="secondary" style={{ margin: 0 }}>
            {t('record.wh.pick.noPickerText')}
          </p>
        )}
      </Panel>
      <Panel title={t('record.wh.details')}>{form}</Panel>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.picking'), to: listPath('warehouse.pick', '/picking') },
          { label: task.number, mono: true },
        ]}
        title={
          <>
            {t('record.wh.pick.title')} <span className="mono rec-mono-title">{task.number}</span>
          </>
        }
        meta={meta}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OperationAlerts failure={operation.failure} notice={operation.notice} />
      {notices}
      {primary && pending.length > 0 && (
        <div className="alert alert-neutral" role="status">
          <Icon name="clock" />
          <span>{t('record.wh.pick.left', { count: pending.length })}</span>
        </div>
      )}
      {task.hasShortages && (
        // A shortage is stock that was not on the shelf: a reason to recount the cell, not
        // a line of statistics.
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{t('card.wh.pick.shortageNotice')}</span>
        </div>
      )}

      <StageBar
        ariaLabel={t('record.wh.stages')}
        steps={stagesOf(
          STAGES,
          task.status === 'cancelled' ? -1 : STAGES.indexOf(task.status as (typeof STAGES)[number]),
          (code) => t(`value.warehouse.pick.status.${code}`),
          (code) =>
            code === 'in_progress'
              ? momentOr(started, locale, '')
              : code === 'completed'
                ? momentOr(finished, locale, '')
                : code === 'new'
                  ? momentOr(record.values.created_at, locale, '')
                  : '',
        )}
        ends={cancelledEnd(task.status === 'cancelled', t('record.wh.cancelledEnd'))}
      />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'route', label: t('card.tab.whRoute'), count: stops.length },
          { id: 'skipped', label: t('record.wh.pick.skippedTab'), count: skipped.length },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.wh.pick.sections')}
      >
        {tab === 'history' ? (
          <HistoryTab entity="warehouse.pick" recordId={recordId} feedType="pick_task" />
        ) : (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">{tab === 'route' ? routePanel : skippedPanels}</div>
            {sidebar}
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirmCancel}
        icon="close"
        title={t('record.wh.pick.cancel.title', { number: task.number })}
        confirmLabel={t('card.wh.pick.cancelSubmit')}
        pending={cancel.isPending}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('card.wh.pick.cancelHint')}</p>
        {taken.length > 0 && <p>{t('record.wh.pick.cancel.taken', { count: taken.length })}</p>}
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.wh.pick.cancel.final')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/**
 * Остановки маршрута. С маршрутом сервера — его порядок и коды ячеек; без него — строки
 * задания в присланном порядке (`routeOrder` уже их порядок), ячейка и товар — подписями
 * из самой строки задания.
 */
function stopsOf(task: PickTask, route: PickRoute | undefined): Stop[] {
  const lines = new Map(task.lines.map((line) => [line.id, line]))
  if (route) {
    const names = route.catalogue?.names ?? {}
    return route.stops.map((stop, index) => ({
      lineId: stop.lineId,
      order: stop.order,
      cell: stop.cellCode ?? '—',
      productId: stop.productId,
      name: names[stop.productId],
      quantity: stop.quantity,
      picked: lines.get(stop.lineId)?.picked ?? null,
      status: stop.status,
      next: route.nextStopIndex === index,
    }))
  }
  const firstPending = task.lines.findIndex((line) => line.status === 'pending')
  return task.lines.map((line, index) => ({
    lineId: line.id,
    order: line.routeOrder,
    cell: line.cellId ? (line.cellCode ?? short(line.cellId)) : '—',
    productId: line.productId,
    name: line.productName ?? undefined,
    quantity: line.quantity,
    picked: line.picked,
    status: line.status,
    next: index === firstPending,
  }))
}

/** Строка маршрута `.pick-row`: номер, код ячейки, товар, количество, состояние словом. */
function RouteRow({ stop }: { stop: Stop }) {
  const { t, locale } = useApp()
  const done = stop.status === 'picked' || stop.status === 'short'
  const classes = ['pick-row', done ? 'is-done' : stop.next ? 'is-current' : stop.status === 'skipped' ? 'is-skipped' : '']
    .filter(Boolean)
    .join(' ')
  let badge: React.ReactNode
  switch (stop.status) {
    case 'picked':
      badge = (
        <span className="badge b-success">
          <Icon name="check" />
          {t('record.wh.pick.taken', { qty: qty(stop.picked ?? stop.quantity, locale) })}
        </span>
      )
      break
    case 'short':
      badge = (
        <span className="badge b-warning">
          <Icon name="alert" />
          {t('record.wh.pick.takenOf', { picked: qty(stop.picked, locale), qty: qty(stop.quantity, locale) })}
        </span>
      )
      break
    case 'skipped':
      badge = (
        <span className="badge b-warning">
          <Icon name="alert" />
          {t('warehouse.pickline.status.skipped')}
        </span>
      )
      break
    default:
      badge = stop.next ? (
        <span className="badge b-info">
          <span className="dot" aria-hidden="true" />
          {t('record.wh.pick.next')}
        </span>
      ) : (
        <span className="badge">{t('record.wh.pick.ahead')}</span>
      )
  }
  return (
    <div className={classes} role="listitem" aria-current={stop.next ? 'step' : undefined}>
      <span className="ord">{done ? <Icon name="check" /> : stop.order}</span>
      <span className="cell-code">{stop.cell}</span>
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span className={stop.next ? 'strong' : undefined}>{stop.name ?? short(stop.productId)}</span>
        {stop.name && <span className="mono muted t-xs">{short(stop.productId)}</span>}
      </span>
      <span className={stop.next ? 'num strong' : 'num'}>{qty(stop.quantity, locale)}</span>
      <span>{badge}</span>
    </div>
  )
}

/**
 * Пропущенная строка и где ещё лежит этот товар на складе — по остаткам сервера, со
 * свободным количеством: «лежит» и «можно взять» разные цифры, и резерв под чужую отгрузку
 * взять нельзя.
 */
function SkippedPanel({ stop, warehouseId }: { stop: Stop; warehouseId: string }) {
  const { t, user, locale } = useApp()
  const mayReadStock = can(user, 'warehouse.stock', 'read')
  const stock = useQuery({
    queryKey: ['stock-of', stop.productId, warehouseId],
    queryFn: ({ signal }) => fetchStockOf(stop.productId, warehouseId, signal),
    enabled: mayReadStock,
    retry: false,
  })
  const rows = (stock.data ?? []).filter((row) => row.quantity > 0)
  const title = `${stop.name ?? short(stop.productId)} — ${qty(stop.quantity, locale)}`
  return (
    <Panel
      title={title}
      flush
      actions={
        <span className="badge b-warning">
          <Icon name="alert" />
          {t('warehouse.pickline.status.skipped')}
        </span>
      }
    >
      <div className="panel-b">
        <div className="alert alert-warning">
          <Icon name="alert" />
          <span className="fg">
            {t('record.wh.pick.skippedAt')} <span className="mono">{stop.cell}</span>
          </span>
        </div>
      </div>
      {!mayReadStock ? (
        <div className="panel-b secondary">{t('record.wh.pick.stockForbidden')}</div>
      ) : stock.isLoading ? (
        <div className="panel-b muted">{t('app.loading')}</div>
      ) : stock.isError ? (
        <StateView
          compact
          kind={stock.error instanceof ApiError && stock.error.isForbidden ? 'forbidden' : 'error'}
          title={t('record.wh.pick.stockError')}
        />
      ) : rows.length === 0 ? (
        <div className="panel-b secondary">{t('record.wh.pick.noStockElsewhere')}</div>
      ) : (
        <table className="tbl" aria-label={t('record.wh.pick.elsewhere')}>
          <thead>
            <tr>
              <th>{t('card.wh.line.cell')}</th>
              <th className="num">{t('record.wh.pick.onBooks')}</th>
              <th className="num">{t('record.wh.pick.available')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.available > 0 ? undefined : 'is-muted'}>
                <td>
                  <CellCode id={row.cellId} code={row.cellCode} />
                </td>
                <td className="num">{qty(row.quantity, locale)}</td>
                <td className="num strong">{qty(row.available, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="panel-f secondary">{t('record.wh.pick.addCellMissing')}</div>
    </Panel>
  )
}
